import { Injectable, effect, signal } from "@angular/core";

export type Theme = "light" | "dark";

const STORAGE_KEY = "bf-theme";

/**
 * Tema claro/noturno (spec 011 RF15/RF16).
 *
 * O tema vira o atributo `data-theme` no `<html>`, e é dele que as variáveis
 * de `styles.css` derivam toda a paleta — nenhum componente precisa saber
 * qual tema está ativo para se adaptar.
 *
 * Preferência do sistema é só o **ponto de partida**: assim que a pessoa
 * escolhe no toggle, a escolha manda e é lembrada entre sessões.
 */
@Injectable({ providedIn: "root" })
export class ThemeService {
  private readonly current = signal<Theme>(readInitialTheme());

  /** Tema em vigor. */
  readonly theme = this.current.asReadonly();

  constructor() {
    effect(() => applyTheme(this.current()));
    this.followSystemUntilUserChooses();
  }

  set(theme: Theme): void {
    this.current.set(theme);
    persist(theme);
  }

  toggle(): void {
    this.set(this.current() === "dark" ? "light" : "dark");
  }

  isDark(): boolean {
    return this.current() === "dark";
  }

  /**
   * Enquanto não houver escolha salva, acompanha o sistema em tempo real —
   * quem alterna o modo noturno do SO vê o app acompanhar na hora.
   */
  private followSystemUntilUserChooses(): void {
    const query = matchMediaSafe();
    query?.addEventListener("change", (event) => {
      if (readStored() === null) {
        this.current.set(event.matches ? "dark" : "light");
      }
    });
  }
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
  // Barra do navegador/PWA acompanha a superfície do tema.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", theme === "dark" ? "#1A121C" : "#F8F5F9");
}

function readInitialTheme(): Theme {
  return readStored() ?? (matchMediaSafe()?.matches ? "dark" : "light");
}

function readStored(): Theme | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "dark" || stored === "light" ? stored : null;
  } catch {
    // modo privativo / storage bloqueado: cai na preferência do sistema
    return null;
  }
}

function persist(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // sem persistência disponível; o tema vale para esta sessão
  }
}

function matchMediaSafe(): MediaQueryList | null {
  return typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;
}
